import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowDown, Compass, Monitor, MessageSquare } from 'lucide-react';
import alphaRewards from '../../assets/alpha_rewards.png';
import agricultureLogo from '../../assets/agriculture_logo.png';
import cyberportLogo from '../../assets/cyberport_logo.png';
import hkStpLogo from '../../assets/HK_STP_LOGO.png';

const AboutPage: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      {/* Breadcrumb */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pt-24 pb-8">
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="text-gray-500 hover:text-gray-700 transition-colors"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400
            }}
          >
            Home
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span
            className="text-gray-700"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            About
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Text Content */}
          <div>
            <p
              className="mb-4"
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                color: '#667479'
              }}
            >
              Discover Smarter, Safer, and Greener Camping.
            </p>
            <h1
              className="mb-6"
              style={{
                fontFamily: 'Poppins',
                fontSize: '56px',
                fontWeight: 700,
                lineHeight: '64px',
                color: '#000000'
              }}
            >
              About Us
            </h1>
            <p
              className="mb-8"
              style={{
                fontFamily: 'Inter',
                fontSize: '18px',
                fontWeight: 400,
                lineHeight: '28px',
                color: '#000000'
              }}
            >
              We bring AI and nature together — helping you explore the outdoors with real-time insights and sustainable design.
            </p>

            {/* Scroll Down Button */}
            <div className="flex items-center gap-3">
              <span
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#000000'
                }}
              >
                Scroll Down
              </span>
              <div className="w-12 h-12 rounded-full border-2 border-black flex items-center justify-center">
                <ArrowDown className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Right: Rewards Image */}
          <div>
            <img
              src={alphaRewards}
              alt="Alpha AI Rewards"
              className="w-full h-auto rounded-2xl"
            />
            {/* Image Carousel Dots */}
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <div className="w-2 h-2 rounded-full bg-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Our Partners Section */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 py-16 bg-gray-50">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Text */}
          <div>
            <h2
              className="mb-4"
              style={{
                fontFamily: 'Poppins',
                fontSize: '48px',
                fontWeight: 700,
                color: '#000000'
              }}
            >
              Our Partners
            </h2>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '24px',
                color: '#000000'
              }}
            >
              Together, we are building Hong Kong's next-generation smart outdoor infrastructure.
            </p>
          </div>

          {/* Right: Partner Logos */}
          <div className="space-y-6">
            {/* Supported by */}
            <div>
              <p
                className="mb-3"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#667479'
                }}
              >
                Supported by
              </p>
              <div className="flex items-center">
                <img
                  src={agricultureLogo}
                  alt="Agriculture, Fisheries and Conservation Department"
                  className="h-12"
                />
              </div>
            </div>

            {/* Collaborated with */}
            <div>
              <p
                className="mb-3"
                style={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#667479'
                }}
              >
                Collaborated with
              </p>
              <div className="flex items-center gap-8">
                <img
                  src={cyberportLogo}
                  alt="Cyberport"
                  className="h-10"
                />
                <img
                  src={hkStpLogo}
                  alt="Hong Kong Science and Technology Parks"
                  className="h-10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How it works Section */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 py-16">
        <h2
          className="mb-12"
          style={{
            fontFamily: 'Poppins',
            fontSize: '48px',
            fontWeight: 700,
            color: '#000000'
          }}
        >
          How it works?
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Explore Card */}
          <div>
            <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mb-4">
              <Compass className="w-8 h-8 text-white" />
            </div>
            <h3
              className="mb-3"
              style={{
                fontFamily: 'Poppins',
                fontSize: '24px',
                fontWeight: 700,
                color: '#000000'
              }}
            >
              Explore
            </h3>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '22px',
                color: '#667479'
              }}
            >
              Find and compare campsites across Hong Kong by region, occupancy, and air quality.
            </p>
          </div>

          {/* Monitor Card */}
          <div>
            <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mb-4">
              <Monitor className="w-8 h-8 text-white" />
            </div>
            <h3
              className="mb-3"
              style={{
                fontFamily: 'Poppins',
                fontSize: '24px',
                fontWeight: 700,
                color: '#000000'
              }}
            >
              Monitor
            </h3>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '22px',
                color: '#667479'
              }}
            >
              See live environmental data and safety alerts from smart sensors installed at key campsites.
            </p>
          </div>

          {/* Community / News Card */}
          <div>
            <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-white" />
            </div>
            <h3
              className="mb-3"
              style={{
                fontFamily: 'Poppins',
                fontSize: '24px',
                fontWeight: 700,
                color: '#000000'
              }}
            >
              Community / News
            </h3>
            <p
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '22px',
                color: '#667479'
              }}
            >
              Stay connected with outdoor events, sustainability campaigns, and user stories.
            </p>
          </div>
        </div>
      </div>

      {/* Our Mission Section */}
      <div className="px-4 sm:px-8 lg:px-16 xl:px-24 py-24 bg-gray-50">
        <div className="text-center max-w-5xl mx-auto">
          <p
            className="mb-4"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 400,
              color: '#667479'
            }}
          >
            Our Mission
          </p>
          <h2
            className="mb-8"
            style={{
              fontFamily: 'Poppins',
              fontSize: '40px',
              fontWeight: 700,
              lineHeight: '52px',
              color: '#000000'
            }}
          >
            To make outdoor exploration{' '}
            <span style={{ color: '#93C5FD' }}>SAFER</span>, more{' '}
            <span style={{ color: '#93C5FD' }}>CONNECTED</span>, and more{' '}
            <span style={{ color: '#93C5FD' }}>SUSTAINABLE</span>
            {' '}through{' '}
            <span style={{ color: '#93C5FD' }}>INTELLIGENT TECHNOLOGY</span>.
          </h2>

          {/* Mission Tags */}
          <div className="flex flex-col items-center gap-2">
            <span
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                color: '#667479'
              }}
            >
              # Smart monitoring
            </span>
            <span
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                color: '#667479'
              }}
            >
              # Eco-awareness
            </span>
            <span
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                color: '#667479'
              }}
            >
              # Data transparency
            </span>
            <span
              style={{
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                color: '#667479'
              }}
            >
              # Human-nature harmony
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
